'use client'
import {
  Box,
  InputAdornment,
  TextField,
  Theme,
  styled,
  useTheme,
} from '@mui/material'
import { ChangeEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { textStyles } from '@components/Helpers'
import {
  DewiBreakpointsLg,
  DewiBreakpointsMd,
  DewiBreakpointsSm,
} from '@furry/tokens'

const SearchField = styled(TextField)(
  ({ theme, $open }: { theme: Theme; $open: boolean }) => ({
    [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
      borderRadius: 'var(--dewi_spacing_12)',
    },
    // A filled field: no outline, at rest or focused.
    backgroundColor: 'var(--dewi_colors_dark_7)',
    borderRadius: 'var(--dewi_radius_rounded)',
    height: 43,
    label: {
      display: 'flex',
      flexDirection: 'row',
      fontFamily: 'var(--font-nunito), sans-serif',
      color: $open ? 'var(--dewi_colors_gray_2)' : 'var(--dewi_colors_gray_6)',
      ...textStyles,
    },
    '.MuiOutlinedInput-notchedOutline': {
      borderRadius: 'var(--dewi_radius_rounded)',
      border: 'none !important',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  }),
)

const LabelDisplay = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
  marginLeft: 'var(--dewi_spacing_8)',
}))

const StyledSearchIcon = styled(Search)(({ theme }) => ({
  color: 'var(--dewi_colors_gray_6)',
  [theme.breakpoints.up(parseInt(DewiBreakpointsLg))]: {
    width: 'var(--dewi_spacing_6)',
    height: 'var(--dewi_spacing_6)',
  },
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    marginLeft: '0.2rem',
    width: 'var(--dewi_spacing_8)',
    height: 'var(--dewi_spacing_8)',
  },
}))

const FocusedStyles = (theme: Theme, focused: boolean) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsSm))]: {
    width: focused ? 'var(--dewi_spacing_24)' : 'var(--dewi_spacing_16)',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsSm),
    parseInt(DewiBreakpointsMd),
  )]: {
    width: focused ? 'var(--dewi_spacing_32)' : 'var(--dewi_spacing_16)',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsMd),
    parseInt(DewiBreakpointsLg),
  )]: {
    width: focused ? 'var(--dewi_spacing_52)' : 'var(--dewi_spacing_16)',
  },
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    transition: 'all .2s ease-in-out',
  },
})

type SearchInputProps = {
  sx?: any
  onChange?: (value: string) => void
  value?: string
}

export default function SearchInput({ sx, onChange, value }: SearchInputProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [focused, setFocused] = useState(false)
  const width = typeof window !== 'undefined' ? window?.innerWidth : 0

  const shrinkFunc = () =>
    width > parseInt(DewiBreakpointsLg) && (focused || !!value)

  const shrink = useMemo(shrinkFunc, [focused, width])

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  const searchLabel = (
    <LabelDisplay>{t('explorerPage.overview.form.search')}</LabelDisplay>
  )

  return (
    <SearchField
      theme={theme}
      autoComplete="off"
      $open={focused}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      sx={{ ...sx, ...FocusedStyles(theme, focused) }}
      type="text"
      value={value}
      label={searchLabel}
      InputLabelProps={{
        shrink,
      }}
      onChange={handleSearchInputChange}
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment
            sx={{ margin: 0, pointerEvents: 'none' }}
            position="start"
          >
            <StyledSearchIcon strokeWidth={1.75} />
          </InputAdornment>
        ),
      }}
    />
  )
}
